// #3548 (station-monitor workstream) -- pure helpers for the Station Monitor's
// station cards, unit tested independently of React.

// A station card shows ONE block per scribe. buildStationGroups (stationMonitor.js)
// yields one row per stretch of work (a scribe who moved has several); the counts
// belong to the scribe's snapshot, which rides on that scribe's first row
// (`carriesState`). This folds the rows back into
//   { key, scribeSub, scribeName, snapshot, snapshotSeenElsewhere,
//     stretches: [{ rowKey, ring, position, from, end }] }
// in first-seen order. A station that has no work and no snapshot yet (it
// appears from sign-in, #3549) is one block with no scribe and no stretches, so
// the card renders zero ring sections and zero counts cleanly.
export function buildScribeBlocks(rows) {
  const blocks = new Map();
  for (const row of rows ?? []) {
    let block = blocks.get(row.key);
    if (!block) {
      block = {
        key: row.key,
        scribeSub: row.scribeSub,
        scribeName: row.scribeName,
        snapshot: null,
        snapshotSeenElsewhere: false,
        stretches: [],
      };
      blocks.set(row.key, block);
    }
    if (!block.scribeName && row.scribeName) block.scribeName = row.scribeName;
    if (row.carriesState) {
      block.snapshot = row.snapshot ?? null;
      block.snapshotSeenElsewhere = row.snapshotSeenElsewhere === true;
    }
    if (row.from || row.ring != null || row.position != null) {
      block.stretches.push({ rowKey: row.rowKey, ring: row.ring, position: row.position, from: row.from, end: row.end });
    }
  }
  return [...blocks.values()];
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// The "Oldest unsent" box, and only that box. Returns { unit, count } for the
// caller to translate with a plural-aware key, or null when there is no age.
// Under 1 minute: seconds. Under 1 hour: minutes (floored). Under 24 hours:
// hours floored to the half hour (1, 1.5, ... 23.5). 24 hours or more: days
// (floored). Last seen and the banners keep scribePresence's formatElapsedLabel.
export function oldestUnsentParts(ms) {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return null;
  // Never "0 seconds": a sheet that is unsent has been unsent for at least a second.
  if (ms < MINUTE_MS) return { unit: "second", count: Math.max(1, Math.floor(ms / SECOND_MS)) };
  if (ms < HOUR_MS) return { unit: "minute", count: Math.floor(ms / MINUTE_MS) };
  if (ms < DAY_MS) return { unit: "hour", count: Math.floor(ms / (HOUR_MS / 2)) / 2 };
  return { unit: "day", count: Math.floor(ms / DAY_MS) };
}

// Plain-words copy per failure code for the Send blocked dialog. Returns the
// i18n key suffix group for a code (a closed set, so every key stays static in
// the components); an unknown or missing code is "other".
export const BLOCKED_REASON_CODES = Object.freeze([
  "ride_scratched",
  "ride_ended",
  "duplicate_score_sheet",
  "term_reason_conflict",
  "marks_confirmed_frozen",
  "sheet_completed",
  "marks_not_confirmed",
  "panel_not_fully_signed",
  "sheet_editing",
  "concurrent_modification",
  "stale_write",
  "not_assigned",
  "forbidden",
  "not_found",
  "test_mismatch",
  "missing_signature",
  "judge_not_on_panel",
  "validation_error",
]);

// invalid_body is the same refusal as validation_error to a person.
export function blockedReasonCode(failureCode) {
  if (failureCode === "invalid_body") return "validation_error";
  return BLOCKED_REASON_CODES.includes(failureCode) ? failureCode : "other";
}
